/**
 * Tengill (connector) creation UI: the "find the target" banner with page buttons (one finger keeps
 * scrolling normally in this step), and the optional label sheet with suggestions.
 */
import { CornerDownRight, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { MAX_LABEL_CHARS } from '../../shared/annotations';
import { Button, cx, Sheet, TextInput } from '../components/ui';
import { useT } from '../i18n';
import type { Annotate } from './useAnnotate';

export function ConnectorTargetBanner({ ann }: { ann: Annotate }) {
  const t = useT();
  const flow = ann.flow;
  const stripRef = useRef<HTMLDivElement>(null);
  const count = ann.pages.length;
  const current = ann.currentPage;

  const active = flow?.step === 'target';
  // Keep the current page's button in view (scroll the strip only, never the page).
  useEffect(() => {
    const strip = stripRef.current;
    const el = strip?.querySelector<HTMLElement>(`[data-page-btn="${current}"]`);
    if (strip && el) strip.scrollLeft = el.offsetLeft - strip.clientWidth / 2 + el.offsetWidth / 2;
  }, [current, active]);

  if (!flow || flow.step !== 'target') return null;
  return (
    <div className="pointer-events-none absolute z-30 p-2" style={{ left: 'var(--annot-left, 0px)', right: 'var(--annot-right, 0px)', top: 'var(--annot-top, 0px)' }}>
      <div
        role="status"
        className="pointer-events-auto mx-auto flex max-w-3xl flex-col gap-2 rounded-2xl bg-[var(--color-navy-700)] p-3 text-white shadow-[0_6px_24px_rgb(0_0_0/0.3)]"
      >
        <div className="flex items-start gap-3">
          <CornerDownRight className="mt-0.5 size-6 shrink-0" aria-hidden />
          <p className="flex-1 text-[17px] leading-snug font-semibold">{flow.editId ? t('annotate.conn_find_new_target') : t('annotate.conn_find_target')}</p>
          <button
            type="button"
            onClick={ann.cancelFlow}
            className="flex min-h-11 shrink-0 items-center gap-1 rounded-xl bg-white/15 px-3 text-[15px] font-semibold hover:bg-white/25"
          >
            <X className="size-5" aria-hidden />
            {t('common.cancel')}
          </button>
        </div>
        {count > 1 ? (
          <div ref={stripRef} className="relative flex gap-1.5 overflow-x-auto pb-0.5" role="group" aria-label={t('annotate.conn_pages')}>
            {Array.from({ length: count }, (_, i) => (
              <button
                key={i}
                type="button"
                data-page-btn={i}
                aria-label={t('annotate.conn_page', { page: i + 1 })}
                aria-current={i === current ? 'page' : undefined}
                onClick={() => ann.goToPage(i)}
                className={cx(
                  'flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl px-2 text-[16px] font-bold tabular-nums transition',
                  i === current ? 'bg-white text-[var(--color-navy-700)]' : 'bg-white/15 hover:bg-white/25',
                  flow.source.page === i && i !== current && 'ring-2 ring-white/70',
                )}
              >
                {i + 1}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ConnectorLabelSheet({ ann }: { ann: Annotate }) {
  const t = useT();
  const open = ann.flow?.step === 'label';
  const [value, setValue] = useState('');
  useEffect(() => {
    if (open) setValue('');
  }, [open]);
  return (
    <Sheet
      open={open}
      // Tapping outside / Escape keeps the connector (without a label) — losing the source and
      // target the user just picked would be frustrating; "Hætta við" still cancels explicitly.
      onClose={() => ann.saveConnector(value.trim() ? value : null)}
      title={t('annotate.conn_label_title')}
      footer={
        <>
          <Button variant="secondary" block onClick={ann.cancelFlow}>
            {t('common.cancel')}
          </Button>
          <Button variant="secondary" block onClick={() => ann.saveConnector(null)}>
            {t('annotate.conn_no_label')}
          </Button>
          <Button block disabled={!value.trim()} onClick={() => ann.saveConnector(value)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 py-1">
        <p className="text-[var(--muted)]">{t('annotate.conn_label_hint')}</p>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('annotate.conn_label_suggestions')}>
          {ann.labelSuggestions.map((s) => {
            const on = value.trim() === s;
            return (
              <button
                key={s}
                type="button"
                aria-pressed={on}
                onClick={() => setValue(s)}
                className={cx(
                  'min-h-11 rounded-full border px-4 text-[17px] font-semibold transition',
                  on ? 'border-transparent bg-[var(--accent)] text-[var(--accent-ink)]' : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--accent-soft)]',
                )}
              >
                {s}
              </button>
            );
          })}
        </div>
        <TextInput
          label={t('annotate.conn_label_input')}
          value={value}
          maxLength={MAX_LABEL_CHARS}
          placeholder={t('annotate.conn_label_placeholder')}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value.trim()) ann.saveConnector(value);
          }}
          enterKeyHint="done"
        />
      </div>
    </Sheet>
  );
}
